<template>
  <DynamicDialog
    v-model:show="dialog"
    @dialogOk="generateAISummary()"
    max-width="800px"
    height="70vh"
    :okBtnDisabled="aiSummaryLoading || !aiSummaryPrompt.trim()"
    :okBtnSpinner="aiSummaryLoading"
    okBtnColor="primary"
    okBtnVariant="elevated"
    cancelBtnColor="none"
    cancelBtnVariant="text"
    contentClass="pa-0"
  >
    <template v-slot:title>
      <v-icon class="mr-2">mdi-robot</v-icon>
      AI Report Summary
    </template>
    <template v-slot:content>
      <div v-if="!aiSummaryLoading" class="pa-5">
        <div class="text-h6 mb-1"><b>Client Sector</b></div>
        <div class="text-caption mb-2 text-medium-emphasis">
          Select your organization type to help tailor the AI analysis approach
        </div>
        <v-select
          v-model="selectedClientSector"
          :items="clientSectorItems"
          item-title="name"
          item-value="client_sector"
          variant="outlined"
          density="compact"
          color="primary"
          placeholder="Select your client sector..."
          :disabled="aiSummaryLoading"
          hide-details
          class="mb-4"
        />

        <div class="text-h6 mb-1"><b>System Prompt</b></div>
        <div class="text-caption mb-2 text-medium-emphasis">
          Optional: Define how the AI should behave and analyze the data (e.g.,
          "You are a healthcare data analyst" or "Focus on demographic trends")
        </div>
        <v-textarea
          v-model="aiSystemPrompt"
          rows="3"
          variant="outlined"
          color="secondary"
          placeholder="Enter system prompt to define AI behavior (optional)..."
          :disabled="aiSummaryLoading"
          hide-details
          class="mb-4"
        />

        <div class="text-h6 mb-1"><b>Your Prompt</b></div>
        <v-textarea
          v-model="aiSummaryPrompt"
          rows="3"
          variant="outlined"
          color="primary"
          placeholder="Enter your custom prompt here..."
          :disabled="aiSummaryLoading"
          @keydown.enter.exact="generateAISummary()"
          hide-details
        />
      </div>

      <div v-if="aiSummaryLoading" class="text-center py-8 px-5">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4">Generating AI summary...</p>
      </div>

      <div v-else-if="aiReportSummaryContent.output" class="ai-summary-content">
        <!-- Token usage -->
        <v-card-text v-if="tokenUsage" class="text-left pa-5" style="gap: 8px">
          <div class="text-h6 mb-1"><b>Token Usage</b></div>

          <div style="max-width: 150px" class="text-body-1">
            <!-- Input tokens -->
            <div class="d-flex">
              <div class="d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      start
                      v-bind="props"
                      size="15px"
                      icon="mdi-information-outline"
                      color="primary"
                    ></v-icon>
                  </template>
                  <span>The number of tokens in your input</span>
                </v-tooltip>
                Input
              </div>
              <v-spacer />

              <div>{{ tokenUsage.prompt_tokens.toLocaleString() }}t</div>
            </div>

            <!-- Output tokens -->
            <div class="d-flex">
              <div class="d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      start
                      size="15px"
                      icon="mdi-information-outline"
                      color="primary"
                    ></v-icon>
                  </template>
                  <span>The number of tokens in the generated response</span>
                </v-tooltip>
                Output
              </div>
              <v-spacer />
              <div>{{ tokenUsage.completion_tokens.toLocaleString() }}t</div>
            </div>

            <!-- Total tokens -->
            <div class="mt-2">
              <div class="d-flex">
                <!-- <v-icon start size="15px" color="primary"></v-icon> -->
                <strong>Total</strong>
                <v-spacer />
                <div>{{ tokenUsage.total_tokens.toLocaleString() }}t</div>
              </div>

              <div class="d-flex text-disabled">
                <div class="d-flex align-center">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        start
                        size="15px"
                        icon="mdi-information-outline"
                        color="primary"
                      ></v-icon>
                    </template>
                    <span
                      >Cached tokens significantly decrease latency and reduces
                      costs for subsequent requests</span
                    >
                  </v-tooltip>
                  Cached
                </div>
                <v-spacer />
                <div>
                  {{
                    tokenUsage.prompt_tokens_details.cached_tokens.toLocaleString()
                  }}t
                </div>
              </div>
            </div>

            <div class="d-flex"></div>
          </div>

          <div class="mt-4 text-caption">
            Note: we are billed for the total tokens used in this request, which
            includes both the prompt and the response. The "Input" tokens number
            is usually bigger due to the content of the report we feed into the
            AI.
          </div>
        </v-card-text>

        <v-card-text
          class="text-left pa-4 mx-5 mt-5"
          style="
            white-space: pre-wrap;
            line-height: 1.6;
            border: solid 1px black;
            border-radius: 10px;
          "
        >
          <div class="text-h6 mb-1"><b>Output</b></div>

          {{ aiReportSummaryContent.output }}
        </v-card-text>
        <div class="text-caption px-5 pt-1">
          Even though the cache of the prompts is saved into our OpenAI account
          - the conversation memory is not enabled within this component
          therefore every new request will not remember previous prompts or
          responses.
        </div>
        <v-alert type="info" variant="tonal" rounded="0" class="my-4">
          This summary was generated by AI and may not capture all nuances.
        </v-alert>
      </div>

      <div v-else class="text-center py-8">
        <v-icon size="64" color="grey">mdi-robot-confused</v-icon>
        <p class="mt-4">Ready to generate AI summary</p>
      </div>
    </template>
    <template v-slot:okBtnTitle> Generate Summary </template>
    <template v-slot:cancelBtnTitle>CLOSE</template>
  </DynamicDialog>
</template>

<script>
import DynamicDialog from "@/components/DynamicDialog.vue";
export default {
  name: "AIReportSummary",
  components: {
    DynamicDialog,
  },
  mounted() {},
  props: {
    AISummaryDialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    comparatorsList: {
      required: true,
    },
    indicatorValues: {
      type: Object,
      default: () => ({}),
    },
    content: {
      type: Object,
      default: () => ({}),
    },
    reportType: {
      type: [String, null],
      default: "",
    },
    reportTitle: {
      type: [String, null],
      default: "",
    },
    reportObject: {
      type: [Object, null],
      default: () => ({}),
    },
    reportThemes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      aiSummaryLoading: false,
      // local value
      aiSummaryPrompt:
        "Please provide a comprehensive summary of this report, highlighting key insights, trends, and notable findings from the data.",
      aiSystemPrompt: "",
      selectedClientSector: "",
      clientSectorItems: [
        {
          name: "LA",
          client_sector: "la",
        },
        {
          name: "Grantmaker",
          client_sector: "grantmaker",
        },
        {
          name: "Charity",
          client_sector: "charity",
        },
        {
          name: "Social Housing",
          client_sector: "social-housing",
        },
        {
          name: "Community Foundation",
          client_sector: "community-foundation",
        },
        {
          name: "Private",
          client_sector: "private",
        },
        {
          name: "Healthcare",
          client_sector: "healthcare",
        },
        {
          name: "Other",
          client_sector: "other",
        },
      ],
      tokenUsage: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.AISummaryDialog;
      },
      set(val) {
        this.$emit("update:AISummaryDialog", val);
      },
    },
    aiReportSummaryContent: {
      get() {
        return this.$store.state.aiReportSummaryContent;
      },
      set(value) {
        this.$store.commit("setAIReportSummaryContent", value);
      },
    },
  },
  methods: {
    /* Helper method to strip HTML tags from text */
    stripHtmlTags(html) {
      if (!html) return "";
      return html.replace(/<[^>]*>/g, "").trim();
    },

    /* Generate AI summary with custom prompt */
    generateAISummary() {
      // Always clear existing content before generating new summary
      this.aiSummaryLoading = true;
      // this.aiReportSummaryContent = {};

      // Collect unique areas, indicators, and dates
      const areas = []; // ["Area Name 1", "Area Name 2", ...]
      const indicators = []; // ["Indicator Name 1", "Indicator Name 2", ...]
      const dates = []; // ["2019-01-01", "2021-01-01", ...]

      // Use Maps for fast lookups instead of trying to access the arrays directly
      // This avoids O(n) linear search every time we need to find an index
      const areaMap = new Map();
      const indicatorMap = new Map();
      const dateMap = new Map();

      // Create ultra-compact sparse array structure to eliminate null tokens
      const sparseValues = []; // [areaIndex, indicatorIndex, dateIndex, value]

      // Create a mapping of area codes to comparator names
      const areaCodeToName = {};
      this.comparatorsList.forEach((comparator) => {
        areaCodeToName[comparator.area_code] = comparator.area_name;
      });

      // First pass: collect unique areas, indicators, and dates
      Object.keys(this.indicatorValues).forEach((key) => {
        if (key === "progressID") return;

        const keyParts = key.split(".");
        if (keyParts.length >= 4) {
          const areaCode = keyParts[0];
          const date = keyParts[2];
          const indicatorName = keyParts.slice(3).join(".");
          const areaName = areaCodeToName[areaCode] || areaCode;

          // Add to arrays if not already present
          if (!areaMap.has(areaName)) {
            areaMap.set(areaName, areas.length);
            areas.push(areaName);
          }
          if (!indicatorMap.has(indicatorName)) {
            indicatorMap.set(indicatorName, indicators.length);
            indicators.push(indicatorName);
          }
          if (!dateMap.has(date)) {
            dateMap.set(date, dates.length);
            dates.push(date);
          }
        }
      });

      // Sort dates for consistency
      dates.sort();
      dateMap.clear();
      dates.forEach((date, index) => dateMap.set(date, index));

      // Second pass: populate sparse array with only non-null values
      Object.entries(this.indicatorValues).forEach(([key, value]) => {
        if (key === "progressID") return;

        const keyParts = key.split(".");
        if (keyParts.length >= 4) {
          const areaCode = keyParts[0];
          const date = keyParts[2];
          const indicatorName = keyParts.slice(3).join(".");
          const areaName = areaCodeToName[areaCode] || areaCode;

          const areaIdx = areaMap.get(areaName);
          const indicatorIdx = indicatorMap.get(indicatorName);
          const dateIdx = dateMap.get(date);

          if (
            areaIdx !== undefined &&
            indicatorIdx !== undefined &&
            dateIdx !== undefined
          ) {
            // Only store non-null values: [areaIndex, indicatorIndex, dateIndex, value]
            sparseValues.push([areaIdx, indicatorIdx, dateIdx, value]);
          }
        }
      });

      const extractedData = {
        metadata: {
          title: this.reportTitle || this.content.reportTitle,
          area: this.comparatorsList[0]?.area_name,
          type: this.reportType,
        },
        content: {
          intro: this.reportObject?.introduction,
          about_indicators: this.reportObject?.about_ind,
          about_area: this.reportObject?.how_area_identified,
        },
        data: {
          areas,
          indicators,
          dates,
          values: sparseValues,
        },
        themes: this.reportThemes
          .map((theme) => ({
            name: theme.title,
            desc: theme.description,
            sections: theme.config
              .filter((config) => config.text && config.text.trim() !== "")
              .map((config) => config.text),
          }))
          .filter((theme) => theme.sections.length > 0),
      };

      // Send extracted data directly as JSON to AI report summary endpoint
      this.$axios
        .post("/ai-report-summary", {
          report: JSON.stringify(extractedData),
          prompt: this.aiSummaryPrompt,
          system_prompt: this.aiSystemPrompt,
          client_sector: this.selectedClientSector,
        })
        .then((response) => {
          let summaryContent = "";

          if (response.data.status && response.data.summary) {
            summaryContent = response.data.summary;
          } else {
            summaryContent =
              "AI response received but content could not be extracted.";
          }

          this.aiReportSummaryContent = {
            output: summaryContent,
            prompt: this.aiSummaryPrompt,
          };
          this.tokenUsage = response.data.usage || null;
          this.aiSummaryPrompt = "";
          this.aiSummaryLoading = false;

          this.emit.emit("systemMessage", {
            title: "AI Report Summary Generated",
            message: "AI analysis completed successfully.",
            colour: "success",
            timeout: 3000,
          });
        })
        .catch((error) => {
          console.error("AI Summary Error:", error);
          this.aiSummaryLoading = false;
          this.aiReportSummaryContent = {};

          // Handle structured error responses
          let errorMessage = "Failed to generate AI summary";
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          }

          this.emit.emit("systemMessage", {
            title: "Error",
            message: errorMessage,
            colour: "error",
            timeout: 5000,
          });
        });
    },
  },
  watch: {
    // if we pass a prompt from a saved report, set it as the current prompt
    aiReportSummaryContent(newValue) {
      if (newValue && newValue.prompt) {
        this.aiSummaryPrompt = newValue.prompt;
      } else {
        this.aiSummaryPrompt =
          "Please provide a comprehensive summary of this report, highlighting key insights, trends, and notable findings from the data.";
      }
    },
  },
};
</script>

<style scoped></style>
